import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { mFormat } from '../../../../utils/Money';
import { DataChart } from '../../../../components';
import {
  Box,
  Divider,
  Grid,
  Switch,
  Typography,
  useMediaQuery,
  Icon,
  Link,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { DividerCard, SimpleDialog } from '../../../../components';
import dayjs from 'dayjs';

const useStyles = makeStyles((theme) => ({
  section: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    letterSpacing: 0.6,
    fontWeight: 800,
  },
  table: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  rowLast: {
    borderBottom: 'none',
  },
  rowLabel: {
    fontWeight: 700,
  },
  rowValue: {
    fontWeight: 800,
  },
  bold: {
    fontWeight: '800',
  },
  grey: {
    color: theme.palette.text.primary,
  },
  pieChart: {
    display: 'block',
  },
  '@media print': {
    section: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    pieChart: {
      marginTop: '25px',
    },
  },
}));

export const PlanSummaryToggle = ({
  planSponsor,
  isZeroBalance,
  zeroBalance,
  isTerminatedParticipant,
  terminatedParticipant,
  lastUpdateDate,
  outsideAssets,
}) => {
  const classes = useStyles();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const [openTerminated, setOpenTerminated] = useState(false);
  const [openZeroBalance, setZeroBalance] = useState(false);

  const terminatedModal = {
    Title: 'Include Terminated Participants',
    Content: [
      'Includes participants who have terminated employment but may still have account balances or historical activity in the plan.',
    ],
  };

  const zeroBalanceModal = {
    Title: 'Include Zero Balance Accounts',
    Content: ['Includes participants with a zero account balance.'],
  };

  const renderTerminatedModal = (modal) => (
    <SimpleDialog
      title={modal.Title}
      onClose={() => setOpenTerminated(false)}
      open={openTerminated}
    >
      {modal.Content.map((paragraph, i) => (
        <Typography key={`paragraph-${i}`} variant="body1" paragraph>
          {paragraph}
        </Typography>
      ))}
    </SimpleDialog>
  );

  const renderZeroBalanceModal = (modal) => (
    <SimpleDialog
      title={modal.Title}
      onClose={() => setZeroBalance(false)}
      open={openZeroBalance}
    >
      {modal.Content.map((paragraph, i) => (
        <Typography key={`paragraph-${i}`} variant="body1" paragraph>
          {paragraph}
        </Typography>
      ))}
    </SimpleDialog>
  );

  const planAssets =
    planSponsor?.managedAssets != null && planSponsor?.unmanagedAssets != null
      ? [
          {
            label: (
              <Typography component="div">
                <Typography
                  color="textPrimary"
                  display="inline"
                  style={{ fontWeight: 600 }}
                >
                  Managed:{' '}
                </Typography>
                {mFormat(planSponsor?.managedAssets, 0)}
              </Typography>
            ),
            value: planSponsor?.managedAssets,
          },
          {
            label: (
              <Typography component="div">
                <Typography
                  color="textPrimary"
                  display="inline"
                  style={{ fontWeight: 600 }}
                >
                  Unmanaged:{' '}
                </Typography>
                {mFormat(planSponsor?.unmanagedAssets, 0)}
              </Typography>
            ),
            value: planSponsor?.unmanagedAssets,
          },
        ]
      : null;

  const planParticipants =
    planSponsor?.managedParticipants != null &&
    planSponsor?.unmanagedParticipants != null
      ? [
          {
            label: (
              <Typography component="div">
                <Typography
                  color="textPrimary"
                  display="inline"
                  style={{ fontWeight: 600 }}
                >
                  Managed:{' '}
                </Typography>
                {planSponsor?.managedParticipants}
              </Typography>
            ),
            value: planSponsor?.managedParticipants,
          },
          {
            label: (
              <Typography component="div">
                <Typography
                  color="textPrimary"
                  display="inline"
                  style={{ fontWeight: 600 }}
                >
                  Unmanaged:{' '}
                </Typography>
                {planSponsor?.unmanagedParticipants}
              </Typography>
            ),
            value: planSponsor?.unmanagedParticipants,
          },
        ]
      : null;

  return (
    <>
      {renderTerminatedModal(terminatedModal)}
      {renderZeroBalanceModal(zeroBalanceModal)}
      <Grid
        container
        justifyContent="space-between"
        direction={isDesktop ? 'row' : 'column'}
      >
        <Grid item>
          <Box display="flex" flexDirection="column">
            <Box>
              <Typography
                variant="overline"
                color="textPrimary"
                style={{ fontWeight: 800 }}
              >
                {'PLAN SUMMARY'}
              </Typography>
            </Box>
            <Box sx={isDesktop ? { mb: 0 } : { mb: 2 }}>
              <Typography className={classes.grey} variant="caption">
                {lastUpdateDate != null ? (
                  `Last Updated: ${dayjs(lastUpdateDate).format(
                    'ddd, MMM DD YYYY'
                  )}`
                ) : (
                  <Skeleton width="150px" height="25px" />
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item sx={{ mt: -1 }}>
          <Grid item container>
            <Box
              display="flex"
              alignItems="center"
              sx={isDesktop ? { mb: 0 } : { mb: 1 }}
            >
              <Box>
                <Typography variant="caption" className={classes.bold}>
                  {terminatedModal.Title}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center" pl={1} pt={1}>
                <Link onClick={() => setOpenTerminated(true)}>
                  <Icon color="primary">info_outlined</Icon>
                </Link>
              </Box>
              <Box display="flex" justifyContent="center" pr={2}>
                <Switch
                  checked={terminatedParticipant}
                  onChange={() =>
                    isTerminatedParticipant(!terminatedParticipant)
                  }
                />
              </Box>
            </Box>
          </Grid>
          <Grid item container>
            <Box display="flex" alignItems="center">
              <Box>
                <Typography variant="caption" className={classes.bold}>
                  {zeroBalanceModal.Title}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center" pl={1} pt={1}>
                <Link onClick={() => setZeroBalance(true)}>
                  <Icon color="primary">info_outlined</Icon>
                </Link>
              </Box>
              <Box display="flex" justifyContent="center" pr={2}>
                <Switch
                  checked={zeroBalance}
                  onChange={() => isZeroBalance(!zeroBalance)}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="space-evenly"
        spacing={2}
        direction={isDesktop ? 'row' : 'column'}
        style={{ paddingTop: 16, marginBottom: 64 }}
      >
        <Grid item xs>
          <DividerCard
            header={'Plan Assets'}
            value={mFormat(planSponsor?.planAssets ?? 0)}
            loading={planSponsor == null}
            color="secondary"
            spacing={isDesktop}
          />
        </Grid>
        <Divider sx={{ px: 2 }} flexItem orientation="vertical" />
        <Grid item xs>
          <DividerCard
            header={'Plan Participants'}
            value={planSponsor?.planParticipants ?? 0}
            loading={planSponsor == null}
            spacing={isDesktop}
          />
        </Grid>
        <Divider sx={{ px: 2 }} flexItem orientation="vertical" />
        <Grid item xs>
          <DividerCard
            header={'Outside Accounts'}
            value={mFormat(outsideAssets ?? 0)}
            loading={outsideAssets == null}
            color="secondary"
            spacing={isDesktop}
          />
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="space-evenly"
        alignItems="center"
        spacing={isDesktop ? 2 : 4}
      >
        <Grid item className={classes.pieChart}>
          <DataChart loading={planSponsor == null} data={planAssets ?? []} />
        </Grid>
        {isDesktop && (
          <Divider sx={{ px: 2 }} flexItem orientation="vertical" />
        )}
        <Grid item className={classes.pieChart}>
          <DataChart
            loading={planSponsor == null}
            data={planParticipants ?? []}
          />
        </Grid>
      </Grid>
      <Divider
        style={{ marginTop: 16, marginBottom: 16 }}
        variant="fullWidth"
      />
    </>
  );
};

export default PlanSummaryToggle;
